import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Stepper,
    Step,
    StepLabel,
    Paper,
    Avatar,
    Stack,
    IconButton,
    Fade,
    Divider,
    Alert,
    CircularProgress,
    useTheme,
    Zoom,
    Toolbar,
    AppBar
} from '@mui/material';
import {
    Close as CloseIcon,
    Business as BusinessIcon,
    Dashboard as BoardIcon,
    Person as PersonIcon,
    ArrowBack as BackIcon,
    ArrowForward as ForwardIcon,
    CheckCircle as CheckIcon,
    EventAvailable as EventIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useThemeMode } from '../contexts/ThemeContext';
import ReservationForm, { ReservationFormData } from '../components/ReservationForm';
import CatLoadingSpinner from '../components/CatLoadingSpinner';

const NewReservation: React.FC = () => {
    const navigate = useNavigate();
    const { mode } = useThemeMode();
    const isDark = mode === 'dark';
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [businesses, setBusinesses] = useState<any[]>([]);
    const [boards, setBoards] = useState<any[]>([]);
    const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
    const [selectedBoard, setSelectedBoard] = useState<any>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get('/api/businesses');
                setBusinesses(res.data);
                setLoading(false);
            } catch (err) {
                console.error(err);
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const fetchBoards = async (businessId: string) => {
        try {
            setLoading(true);
            const res = await axios.get(`/api/businesses/${businessId}/boards`);
            setBoards(res.data);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    };

    const handleBusinessSelect = (business: any) => {
        setSelectedBusiness(business);
        fetchBoards(business.id);
        setActiveStep(1);
    };

    const handleBoardSelect = (board: any) => {
        setSelectedBoard(board);
        setActiveStep(2);
    };

    const handleReservationSubmit = async (data: ReservationFormData) => {
        setSubmitting(true);
        setError(null);
        try {
            const endpoint = data.isRecurring 
                ? `/api/boards/${data.boardId}/reservations/recurring`
                : `/api/boards/${data.boardId}/reservations`;
            
            await axios.post(endpoint, data);
            setActiveStep(3); // Result/Success step
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error al crear la reserva');
        } finally {
            setSubmitting(false);
        }
    };

    const steps = ['Seleccionar Negocio', 'Seleccionar Agenda', 'Datos de la Reserva'];

    if (loading && activeStep === 0) return <CatLoadingSpinner message="Preparando la experiencia..." />;

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            bgcolor: isDark ? '#0f172a' : '#f8fafc',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <AppBar position="static" elevation={0} sx={{ bgcolor: 'transparent', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` }}>
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                            <EventIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: isDark ? '#fff' : '#1e293b' }}>
                            Nueva Reserva Inmersiva
                        </Typography>
                    </Stack>
                    <IconButton onClick={() => navigate(-1)} sx={{ color: 'text.secondary' }}>
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Container maxWidth="md" sx={{ py: 6, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Stepper activeStep={activeStep} sx={{ mb: 6 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box sx={{ flex: 1 }}>
                    {activeStep === 0 && (
                        <Fade in>
                            <Box>
                                <Typography variant="h4" align="center" sx={{ fontWeight: 900, mb: 4 }}>
                                    ¿Para qué negocio es la reserva?
                                </Typography>
                                <Grid container spacing={3}>
                                    {businesses.map((b) => (
                                        <Grid size={{ xs: 12, sm: 6 }} key={b.id}>
                                            <Card 
                                                onClick={() => handleBusinessSelect(b)}
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    borderRadius: 4,
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' },
                                                    border: `2px solid transparent`,
                                                    bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fff'
                                                }}
                                            >
                                                <CardContent sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                                                    <Avatar sx={{ bgcolor: 'primary.light', width: 60, height: 60 }}>
                                                        <BusinessIcon fontSize="large" sx={{ color: 'primary.main' }} />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="h5" sx={{ fontWeight: 900 }}>{b.name}</Typography>
                                                        <Typography variant="body2" color="text.secondary">{b.description || 'Sin descripción'}</Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                    {businesses.length === 0 && (
                                        <Grid size={{ xs: 12 }}>
                                            <Alert severity="info">Aún no tienes negocios creados.</Alert>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Fade>
                    )}

                    {activeStep === 1 && (
                        <Fade in>
                            <Box>
                                <Button startIcon={<BackIcon />} onClick={() => setActiveStep(0)} sx={{ mb: 4 }}>
                                    Volver a negocios
                                </Button>
                                <Typography variant="h4" align="center" sx={{ fontWeight: 900, mb: 4 }}>
                                    Selecciona la Agenda en {selectedBusiness?.name}
                                </Typography>
                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                                ) : (
                                    <Grid container spacing={3}>
                                        {boards.map((board) => (
                                            <Grid size={{ xs: 12, sm: 6 }} key={board.id}>
                                                <Card 
                                                    onClick={() => handleBoardSelect(board)}
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        borderRadius: 4,
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' },
                                                        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fff'
                                                    }}
                                                >
                                                    <CardContent sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                                                        <Avatar sx={{ bgcolor: 'secondary.light', width: 60, height: 60 }}>
                                                            <BoardIcon fontSize="large" sx={{ color: 'secondary.main' }} />
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="h5" sx={{ fontWeight: 900 }}>{board.name}</Typography>
                                                            <Typography variant="body2" color="text.secondary">{board.type}</Typography>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Box>
                        </Fade>
                    )}

                    {activeStep === 2 && (
                        <Fade in>
                            <Box>
                                <Button startIcon={<BackIcon />} onClick={() => setActiveStep(1)} sx={{ mb: 4 }}>
                                    Cambiar Agenda
                                </Button>
                                <Paper sx={{ 
                                    p: 4, borderRadius: 5, 
                                    bgcolor: isDark ? 'rgba(30,41,59,0.3)' : '#fff',
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                                }}>
                                    <Typography variant="h5" sx={{ fontWeight: 900, mb: 4 }}>
                                        Completar datos para {selectedBoard?.name}
                                    </Typography>
                                    <ReservationForm 
                                        boards={[selectedBoard]} 
                                        initialData={{ boardId: selectedBoard.id }}
                                        onSubmit={handleReservationSubmit}
                                        onCancel={() => setActiveStep(1)}
                                        loading={submitting}
                                        error={error || undefined}
                                    />
                                </Paper>
                            </Box>
                        </Fade>
                    )}

                    {activeStep === 3 && (
                        <Zoom in>
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Avatar sx={{ bgcolor: 'success.main', width: 100, height: 100, mx: 'auto', mb: 4 }}>
                                    <CheckIcon sx={{ fontSize: 60 }} />
                                </Avatar>
                                <Typography variant="h3" sx={{ fontWeight: 900, mb: 2 }}>¡Reserva Creada!</Typography>
                                <Typography variant="h6" color="text.secondary" sx={{ mb: 6 }}>
                                    La reserva en <b>{selectedBoard?.name}</b> ha sido registrada con éxito.
                                </Typography>
                                <Stack direction="row" spacing={2} justifyContent="center">
                                    <Button variant="contained" size="large" onClick={() => navigate('/admin/live')} sx={{ borderRadius: 3, px: 4 }}>
                                        Ir al Panel En Vivo
                                    </Button>
                                    <Button variant="outlined" size="large" onClick={() => setActiveStep(0)} sx={{ borderRadius: 3, px: 4 }}>
                                        Crear Otra
                                    </Button>
                                </Stack>
                            </Box>
                        </Zoom>
                    )}
                </Box>
            </Container>
        </Box>
    );
};

export default NewReservation;
